import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { SafeHtml } from '@angular/platform-browser';
import { IconService } from '../services/icon.service';

/**
 * Renders an inline SVG from the shared icon library served by the image CDN.
 *
 * Usage:
 *   <app-icon name="arrow-right"></app-icon>                       decorative (default)
 *   <app-icon name="phone" label="Call us"></app-icon>             meaningful icon (gets an accessible name)
 *   <app-icon name="location" [size]="32"></app-icon>              custom size in px
 *
 * The SVG is inlined (not <img src>) so it inherits `color` via `currentColor`,
 * letting the same file be recolored per-usage with plain CSS.
 */
@Component({
  selector: 'app-icon',
  template: `<span class="app-icon__svg" [innerHTML]="svg$ | async"></span>`,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        width: 1.5em;
        height: 1.5em;
      }
      .app-icon__svg {
        display: inline-flex;
        width: 100%;
        height: 100%;
      }
      ::ng-deep .app-icon__svg svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    `,
  ],
})
export class IconComponent implements OnChanges {
  @Input() name!: string;
  /** Optional fixed size in px. Omit to let the surrounding SCSS size `app-icon` via width/height (e.g. for icons that must resize per breakpoint). */
  @Input() size: number | string | null = null;
  @Input() label?: string;

  @HostBinding('style.width.px') get widthPx() {
    return typeof this.size === 'number' ? this.size : null;
  }
  @HostBinding('style.height.px') get heightPx() {
    return typeof this.size === 'number' ? this.size : null;
  }
  @HostBinding('attr.role') get role() {
    return this.label ? 'img' : null;
  }
  @HostBinding('attr.aria-label') get ariaLabel() {
    return this.label || null;
  }
  @HostBinding('attr.aria-hidden') get ariaHidden() {
    return this.label ? null : 'true';
  }

  svg$!: Observable<SafeHtml>;

  constructor(private iconService: IconService) {}

  ngOnChanges(): void {
    if (this.name) {
      this.svg$ = this.iconService.getIcon(this.name);
    }
  }
}
